import React, { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';

/**
 * CreditMeter — Atelier editorial credit indicator.
 * Breathing coral dot + uppercase caption + tabular serif number.
 * Compact, 1px hairline, no shadows.
 */
const CreditMeter = ({ current = 0, onClick }) => {
  const [burst, setBurst] = useState(false);
  const lastRef = useRef(current);
  const count = useMotionValue(current);
  const rounded = useTransform(count, (v) =>
    v >= 10000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(v < 100 ? 2 : 1)
  );

  useEffect(() => {
    const controls = animate(count, current, {
      duration: 0.9,
      ease: [0.25, 1, 0.5, 1],
    });
    if (current > lastRef.current) {
      setBurst(true);
      const t = setTimeout(() => setBurst(false), 1400);
      return () => {
        clearTimeout(t);
        controls.stop();
      };
    }
    lastRef.current = current;
    return () => controls.stop();
  }, [current, count]);

  return (
    <button
      type="button"
      onClick={onClick}
      className="group inline-flex items-center gap-3 h-9 px-3.5 rounded-full border border-atelier-line bg-atelier-surface hover:border-atelier-coral/60 hover:bg-atelier-surface2 transition-all duration-300"
      data-testid="credit-meter"
      aria-label="View credits"
    >
      {/* Breathing coral dot */}
      <span className="relative flex-shrink-0 w-1.5 h-1.5">
        <span
          className="absolute inset-0 rounded-full bg-atelier-coral atelier-breathe"
          style={{ boxShadow: burst ? '0 0 12px rgba(255,84,54,0.65)' : '0 0 6px rgba(255,84,54,0.35)' }}
        />
      </span>

      {/* Uppercase caption */}
      <span
        className="atelier-caption hidden sm:inline group-hover:text-atelier-coral transition-colors"
        style={{ letterSpacing: '0.18em' }}
      >
        Credits
      </span>

      <span className="h-3.5 w-px bg-atelier-line hidden sm:block" />

      {/* Serif tabular number */}
      <motion.span
        className={`tabular-nums text-atelier-ink text-[14px] transition-colors ${
          burst ? 'text-atelier-coral' : ''
        }`}
        style={{
          fontFamily: "'Noto Serif TC', 'Playfair Display', Georgia, serif",
          fontWeight: 500,
          minWidth: '52px',
          textAlign: 'right',
        }}
        data-testid="credit-meter-value"
      >
        {rounded}
      </motion.span>
    </button>
  );
};

export default CreditMeter;
